/**
 * The concierge script.
 *
 * WHY SCRIPTED AND NOT AN LLM (for now)
 * -------------------------------------
 * There is no LLM infrastructure in this repo at all — no SDK, no key, no
 * conversation storage, no moderation surface. A scripted concierge ships
 * today, costs nothing per conversation, cannot hallucinate a product claim or
 * a delivery promise, and is deterministic enough to unit-test. When an LLM
 * version lands, this becomes its fallback rather than being thrown away.
 *
 * THE ONE RULE THAT MAKES IT FEEL LIKE LISTENING
 * ----------------------------------------------
 * Never ask something we already know. Every node declares the profile key it
 * would fill, and the panel skips any node whose key is already on the
 * collector's profile — whether it was learned here, in the quiz, or from the
 * Combination. Being asked your own name twice is the precise feeling this
 * whole project is trying to avoid.
 */

export interface ConciergeOption {
  label: string;
  /** Value persisted against `fillsKey`. Stable — never rename. */
  value?: string;
  /** Node to advance to. `null` ends the conversation on `closing`. */
  next: string | null;
}

export interface ConciergeNode {
  id: string;
  /** What the concierge says. One or two short lines, never a paragraph. */
  says: string;
  /**
   * The profile key this node fills. When the collector already has a fact
   * with this key, the node is skipped entirely.
   */
  fillsKey?: string;
  options?: readonly ConciergeOption[];
  /** Renders a free-text box instead of options — used for the ticket. */
  freeText?: {
    placeholder: string;
    submitLabel: string;
    /** `ticket` routes to createTicket; `fact` stores the text as a fact. */
    action: 'ticket' | 'fact';
  };
}

/** Opening line, personalised when we know who they are. `{name}` optional. */
export const GREETING = {
  known: 'Welcome back, {name}. Anything I can help with?',
  knownWithArchetype: '{name} — still building for the long haul? What can I help with?',
  stranger: 'Hello. I look after questions here. What brings you in?',
} as const;

export const NODES: readonly ConciergeNode[] = [
  {
    id: 'root',
    says: 'What can I help with?',
    options: [
      { label: 'Help me pick something', next: 'pick' },
      { label: 'A question about an order', next: 'order' },
      { label: 'Something else', next: 'freeform' },
    ],
  },

  // ---- Product guidance. Doubles as profile capture. -----------------------
  {
    id: 'pick',
    says: 'Easy. What are you storing?',
    fillsKey: 'primaryUse',
    options: [
      { label: 'Sports cards', value: 'sports', next: 'pickSize' },
      { label: 'TCG — Magic, Pokémon, Lorcana', value: 'tcg', next: 'pickSize' },
      { label: 'Both', value: 'both', next: 'pickSize' },
    ],
  },
  {
    id: 'pickSize',
    says: 'Roughly how many cards are we talking about?',
    fillsKey: 'collectionSize',
    options: [
      { label: 'Under 300', value: 'starting', next: 'pickPriority' },
      { label: '300 to 800', value: 'growing', next: 'pickPriority' },
      { label: 'More than 800', value: 'deep', next: 'pickPriority' },
    ],
  },
  {
    id: 'pickPriority',
    says: 'And what matters most — protection, capacity, or carrying it around?',
    fillsKey: 'priority',
    options: [
      { label: 'Protection', value: 'protection', next: 'recommend' },
      { label: 'Capacity', value: 'capacity', next: 'recommend' },
      { label: 'Carrying it', value: 'portability', next: 'recommend' },
    ],
  },
  {
    id: 'recommend',
    says:
      'That points at the 216-card toploader binder — ringless, zipper-enclosed, no logo on the cover. Want to look?',
    options: [
      { label: 'Show me', next: 'toProduct' },
      { label: 'Not right now', next: 'anythingElse' },
    ],
  },
  {
    id: 'toProduct',
    says: 'It is on the product page — I have opened it for you.',
    options: [{ label: 'Thanks', next: 'anythingElse' }],
  },

  // ---- Orders. Honest about what we can and cannot see. --------------------
  {
    id: 'order',
    says:
      'Orders are fulfilled through Amazon, so I cannot see their status from here — but I can put your question in front of a person today.',
    options: [
      { label: 'Yes, raise it', next: 'ticket' },
      { label: 'Never mind', next: 'anythingElse' },
    ],
  },

  // ---- Free-form → ticket -------------------------------------------------
  {
    id: 'freeform',
    says: 'Tell me what it is and I will make sure someone reads it.',
    options: [
      { label: 'Raise it with a person', next: 'ticket' },
      { label: 'Actually, help me pick something', next: 'pick' },
    ],
  },
  {
    id: 'ticket',
    says: 'What should I pass on?',
    freeText: {
      placeholder: 'Type it here…',
      submitLabel: 'Send it',
      action: 'ticket',
    },
  },

  // ---- Close --------------------------------------------------------------
  {
    id: 'anythingElse',
    says: 'Anything else?',
    options: [
      { label: 'Help me pick something', next: 'pick' },
      { label: 'Raise something with a person', next: 'ticket' },
      { label: 'No, that is everything', next: null },
    ],
  },
  {
    id: 'closing',
    says: 'Good. I am here whenever.',
  },
];

export const TICKET_SENT = {
  /** `{ref}` is the quotable reference. */
  confirmation:
    'Sent. Your reference is {ref}, and it is in your inbox too. A person replies within one business day.',
  /** Shown when the send layer is unconfigured — honest, not silent. */
  noEmail:
    'Sent, and your reference is {ref}. Write it down — our email is not switched on yet, so this is the only copy.',
} as const;

/** Shown when we need an address from a signed-out visitor. */
export const EMAIL_PROMPT = {
  says: 'Where should the reply go?',
  placeholder: 'you@example.com',
  submitLabel: 'Send it',
} as const;

export function nodeById(id: string): ConciergeNode | undefined {
  return NODES.find((node) => node.id === id);
}
